import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ToolStep {
  tool: string;
  settings: any;
}

export interface ToolPreset {
  id: string;
  name: string;
  description?: string;
  tools_chain: ToolStep[];
  is_public: boolean;
  category?: string;
  created_at: string;
  updated_at: string;
}

export const useToolPresets = () => {
  const [presets, setPresets] = useState<ToolPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setPresets([]);
        return;
      }

      const { data, error } = await supabase
        .from('tool_presets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPresets((data || []) as unknown as ToolPreset[]);
    } catch (error) {
      console.error('Error fetching presets:', error);
      toast({
        title: 'Error',
        description: 'Failed to load presets',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createPreset = async (
    name: string,
    toolsChain: ToolStep[],
    description?: string,
    category?: string,
    isPublic: boolean = false
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('tool_presets')
        .insert({
          user_id: user.id,
          name,
          description,
          tools_chain: toolsChain as any,
          category,
          is_public: isPublic,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Preset created successfully',
      });

      await fetchPresets();
      return data;
    } catch (error) {
      console.error('Error creating preset:', error);
      toast({
        title: 'Error',
        description: 'Failed to create preset',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updatePreset = async (
    id: string,
    updates: Partial<Omit<ToolPreset, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ) => {
    try {
      const { error } = await supabase
        .from('tool_presets')
        .update(updates as any)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Preset updated successfully',
      });

      await fetchPresets();
    } catch (error) {
      console.error('Error updating preset:', error);
      toast({
        title: 'Error',
        description: 'Failed to update preset',
        variant: 'destructive',
      });
    }
  };

  const deletePreset = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tool_presets')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Preset deleted successfully',
      });

      await fetchPresets();
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete preset',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchPresets();
  }, []);

  return {
    presets,
    loading,
    createPreset,
    updatePreset,
    deletePreset,
    refetch: fetchPresets,
  };
};